/**
 * 🌍 ZONE MANAGEMENT PAGE
 * Manage zones for a specific airport
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Grid,
  Breadcrumbs,
  Link,
  Paper,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ArrowBack,
  Room,
  DirectionsCar,
  ArrowUpward,
  ArrowDownward,
  Place,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { airportZoneService, AirportZone, CreateZoneData, UpdateZoneData } from '@/services/airportZoneService';
import { airportService, Airport } from '@/services/airportService';

export const ZoneManagementPage: React.FC = () => {
  const { airportId } = useParams<{ airportId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [airport, setAirport] = useState<Airport | null>(null);
  const [zones, setZones] = useState<AirportZone[]>([]);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState<{ open: boolean; zone: AirportZone | null }>({
    open: false,
    zone: null
  });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; zone: AirportZone | null }>({
    open: false,
    zone: null
  });
  const [formData, setFormData] = useState<CreateZoneData>({
    zoneName: '',
    zoneDescription: '',
    minDistanceKm: 0,
    maxDistanceKm: 0,
    landmarks: '',
    displayOrder: 1
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (airportId) {
      loadAirportAndZones();
    }
  }, [airportId]);

  const loadAirportAndZones = async () => {
    if (!airportId) return;

    setLoading(true);
    try {
      // Load airport details
      const airportResponse = await airportService.getAirport(airportId);
      if (airportResponse.success && airportResponse.data) {
        setAirport(airportResponse.data);
      }

      // Load zones
      const zonesResponse = await airportZoneService.getZones(airportId);
      if (zonesResponse.success && zonesResponse.zones) {
        // Ensure we always set an array
        const zonesArray = Array.isArray(zonesResponse.zones) ? zonesResponse.zones : [];
        setZones(zonesArray.sort((a, b) => a.displayOrder - b.displayOrder));
      } else {
        setZones([]); // Reset to empty array on error
        toast.error(zonesResponse.error || 'Failed to load zones');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setZones([]); // Reset to empty array on error
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    const nextOrder = Array.isArray(zones) && zones.length > 0 ? Math.max(...zones.map(z => z.displayOrder)) + 1 : 1;
    setFormData({
      zoneName: '',
      zoneDescription: '',
      minDistanceKm: 0,
      maxDistanceKm: 0,
      landmarks: '',
      displayOrder: nextOrder
    });
    setCreateDialog(true);
  };

  const handleEdit = (zone: AirportZone) => {
    setFormData({
      zoneName: zone.zoneName,
      zoneDescription: zone.zoneDescription,
      minDistanceKm: zone.distanceRange.min,
      maxDistanceKm: zone.distanceRange.max,
      landmarks: zone.landmarks,
      displayOrder: zone.displayOrder
    });
    setEditDialog({ open: true, zone });
  };

  const handleSubmitCreate = async () => {
    if (!airportId) return;

    if (!formData.zoneName || !formData.zoneDescription) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.minDistanceKm >= formData.maxDistanceKm) {
      toast.error('Max distance must be greater than min distance');
      return;
    }

    setSubmitting(true);
    try {
      const response = await airportZoneService.createZone(airportId, formData);
      if (response.success) {
        toast.success('Zone created successfully');
        setCreateDialog(false);
        await loadAirportAndZones();
      } else {
        toast.error(response.error || 'Failed to create zone');
      }
    } catch (error) {
      console.error('Error creating zone:', error);
      toast.error('Failed to create zone');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async () => {
    if (!airportId || !editDialog.zone) return;

    if (!formData.zoneName || !formData.zoneDescription) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.minDistanceKm >= formData.maxDistanceKm) {
      toast.error('Max distance must be greater than min distance');
      return;
    }

    setSubmitting(true);
    try {
      const updateData: UpdateZoneData = {
        zoneName: formData.zoneName,
        zoneDescription: formData.zoneDescription,
        minDistanceKm: formData.minDistanceKm,
        maxDistanceKm: formData.maxDistanceKm,
        landmarks: formData.landmarks,
        displayOrder: formData.displayOrder
      };

      const response = await airportZoneService.updateZone(airportId, editDialog.zone.id, updateData);
      if (response.success) {
        toast.success('Zone updated successfully');
        setEditDialog({ open: false, zone: null });
        await loadAirportAndZones();
      } else {
        toast.error(response.error || 'Failed to update zone');
      }
    } catch (error) {
      console.error('Error updating zone:', error);
      toast.error('Failed to update zone');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!airportId || !deleteDialog.zone) return;

    setSubmitting(true);
    try {
      const response = await airportZoneService.deleteZone(airportId, deleteDialog.zone.id);
      if (response.success) {
        toast.success('Zone deleted successfully');
        setDeleteDialog({ open: false, zone: null });
        await loadAirportAndZones();
      } else {
        toast.error(response.error || 'Failed to delete zone');
      }
    } catch (error) {
      console.error('Error deleting zone:', error);
      toast.error('Failed to delete zone');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReorder = async (zoneId: string, direction: 'up' | 'down') => {
    if (!airportId) return;

    const currentIndex = zones.findIndex(z => z.id === zoneId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= zones.length) return;

    // Swap zones
    const newZones = [...zones];
    [newZones[currentIndex], newZones[newIndex]] = [newZones[newIndex], newZones[currentIndex]];

    // Update display orders
    const zoneOrders = newZones.map((zone, index) => ({
      zoneId: zone.id,
      displayOrder: index + 1
    }));

    setZones(newZones); // Optimistically update UI

    try {
      const response = await airportZoneService.reorderZones(airportId, zoneOrders);
      if (!response.success) {
        toast.error(response.error || 'Failed to reorder zones');
        await loadAirportAndZones(); // Revert on error
      } else {
        toast.success('Zones reordered successfully');
      }
    } catch (error) {
      console.error('Error reordering zones:', error);
      toast.error('Failed to reorder zones');
      await loadAirportAndZones(); // Revert on error
    }
  };

  if (!airportId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Invalid airport ID</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/service-areas/airports')}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          Airports
        </Link>
        <Typography color="text.primary" variant="body2">
          {airport?.airportName || 'Loading...'}
        </Typography>
        <Typography color="text.primary" variant="body2">
          Zones
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <IconButton onClick={() => navigate('/service-areas/airports')} size="small">
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Room /> Zone Management
            </Typography>
          </Box>
          {airport && (
            <Paper sx={{ p: 2, bgcolor: 'primary.50', borderLeft: 3, borderColor: 'primary.main' }}>
              <Typography variant="h6" gutterBottom>
                {airport.airportCode} - {airport.airportName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {airport.city}, {airport.state}, {airport.country}
              </Typography>
            </Paper>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
        >
          Add Zone
        </Button>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Zone Management:</strong> Define pickup/dropoff zones for this airport. Zones are displayed to customers in the order shown below. Use the arrow buttons to reorder.
        </Typography>
      </Alert>

      {/* Zones Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : zones.length === 0 ? (
            <Alert severity="info">
              No zones found. Click "Add Zone" to create your first zone for this airport.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="60">Order</TableCell>
                    <TableCell>Zone Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Distance Range</TableCell>
                    <TableCell>Landmarks</TableCell>
                    <TableCell align="center">Listings</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {zones.map((zone, index) => (
                    <TableRow key={zone.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body2" fontWeight={600}>
                            #{zone.displayOrder}
                          </Typography>
                          <Stack>
                            <IconButton
                              size="small"
                              onClick={() => handleReorder(zone.id, 'up')}
                              disabled={index === 0}
                              sx={{ p: 0.25 }}
                            >
                              <ArrowUpward fontSize="inherit" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleReorder(zone.id, 'down')}
                              disabled={index === zones.length - 1}
                              sx={{ p: 0.25 }}
                            >
                              <ArrowDownward fontSize="inherit" />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {zone.zoneName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {zone.zoneDescription}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<Place />}
                          label={zone.distanceRange.display}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {zone.landmarks}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={zone.stats?.activeListingsCount || 0}
                          size="small"
                          color="success"
                          variant="outlined"
                          icon={<DirectionsCar />}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={zone.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={zone.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Edit Zone">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(zone)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Zone">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteDialog({ open: true, zone })}
                              color="error"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialog} onClose={() => !submitting && setCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Zone</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                label="Zone Name"
                value={formData.zoneName}
                onChange={(e) => setFormData({ ...formData, zoneName: e.target.value })}
                fullWidth
                required
                placeholder="Victoria Island/Ikoyi"
                helperText="Short, descriptive name for the zone"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Zone Description"
                value={formData.zoneDescription}
                onChange={(e) => setFormData({ ...formData, zoneDescription: e.target.value })}
                fullWidth
                required
                multiline
                rows={2}
                placeholder="Upscale business district with high-rise buildings"
                helperText="Brief description of the zone"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Min Distance (km)"
                type="number"
                value={formData.minDistanceKm}
                onChange={(e) => setFormData({ ...formData, minDistanceKm: parseFloat(e.target.value) })}
                fullWidth
                required
                placeholder="8"
                inputProps={{ step: '0.1', min: 0 }}
                helperText="Minimum distance from airport"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Max Distance (km)"
                type="number"
                value={formData.maxDistanceKm}
                onChange={(e) => setFormData({ ...formData, maxDistanceKm: parseFloat(e.target.value) })}
                fullWidth
                required
                placeholder="15"
                inputProps={{ step: '0.1', min: 0 }}
                helperText="Maximum distance from airport"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Landmarks"
                value={formData.landmarks}
                onChange={(e) => setFormData({ ...formData, landmarks: e.target.value })}
                fullWidth
                placeholder="VI, Ikoyi, Banana Island"
                helperText="Comma-separated list of notable landmarks in this zone"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Display Order"
                type="number"
                value={formData.displayOrder}
                onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) })}
                fullWidth
                inputProps={{ min: 1 }}
                helperText="Order in which this zone appears to customers"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitCreate}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <Add />}
          >
            {submitting ? 'Creating...' : 'Create Zone'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => !submitting && setEditDialog({ open: false, zone: null })} maxWidth="md" fullWidth>
        <DialogTitle>Edit Zone</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                label="Zone Name"
                value={formData.zoneName}
                onChange={(e) => setFormData({ ...formData, zoneName: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Zone Description"
                value={formData.zoneDescription}
                onChange={(e) => setFormData({ ...formData, zoneDescription: e.target.value })}
                fullWidth
                required
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Min Distance (km)"
                type="number"
                value={formData.minDistanceKm}
                onChange={(e) => setFormData({ ...formData, minDistanceKm: parseFloat(e.target.value) })}
                fullWidth
                required
                inputProps={{ step: '0.1', min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Max Distance (km)"
                type="number"
                value={formData.maxDistanceKm}
                onChange={(e) => setFormData({ ...formData, maxDistanceKm: parseFloat(e.target.value) })}
                fullWidth
                required
                inputProps={{ step: '0.1', min: 0 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Landmarks"
                value={formData.landmarks}
                onChange={(e) => setFormData({ ...formData, landmarks: e.target.value })}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, zone: null })} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitEdit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <Edit />}
          >
            {submitting ? 'Updating...' : 'Update Zone'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => !submitting && setDeleteDialog({ open: false, zone: null })} maxWidth="sm">
        <DialogTitle>Delete Zone?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete <strong>{deleteDialog.zone?.zoneName}</strong>?
          </Alert>
          {deleteDialog.zone && deleteDialog.zone.stats && deleteDialog.zone.stats.activeListingsCount > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              This zone has <strong>{deleteDialog.zone.stats.activeListingsCount} active listings</strong>. Deleting it may affect hosts who are offering trips to this zone!
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, zone: null })} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <Delete />}
          >
            {submitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ZoneManagementPage;
